import React, { useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, LinearProgress, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { AddPhotoAlternate } from '@mui/icons-material';
import { postService, Post } from '../services/postService';
import { storyService } from '../services/storyService';
import { ACCEPTED_MEDIA_TYPES, isVideoFile, validateMediaFile } from '../utils/media';
import { useNotification } from './NotificationContext';

interface StoryCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (story: Post) => void;
}

/** One photo or short video, an optional caption, and who may watch it — gone after 24 hours. */
const StoryCreateDialog: React.FC<StoryCreateDialogProps> = ({ open, onClose, onCreated }) => {
  const { showSuccess, showError } = useNotification();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [visibility, setVisibility] = useState<'Buddies' | 'Followers'>('Buddies');
  const [submitting, setSubmitting] = useState(false);
  const [uploadFraction, setUploadFraction] = useState<number | null>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    if (!picked) return;
    const problem = await validateMediaFile(picked);
    if (problem) { showError(problem); return; }
    if (preview) URL.revokeObjectURL(preview);
    setFile(picked);
    setPreview(URL.createObjectURL(picked));
  };

  const close = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null); setPreview(null); setCaption('');
    onClose();
  };

  const submit = async () => {
    if (!file) return;
    setSubmitting(true);
    try {
      setUploadFraction(0);
      const mediaKey = await postService.uploadPhoto(file, setUploadFraction);
      const story = await storyService.create({ mediaKey, caption: caption.trim() || undefined, visibility });
      showSuccess('Story shared — it disappears in 24 hours');
      onCreated(story);
      close();
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to share story');
    } finally {
      setSubmitting(false);
      setUploadFraction(null);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : close} fullWidth maxWidth="xs">
      <DialogTitle>Add to your story</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <input ref={inputRef} type="file" accept={ACCEPTED_MEDIA_TYPES} hidden onChange={handleFile} />
          {preview ? (
            <Box sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: 'common.black', display: 'flex', justifyContent: 'center' }}>
              {file && isVideoFile(file)
                ? <video src={preview} muted playsInline controls style={{ maxWidth: '100%', maxHeight: 320 }} />
                : <img src={preview} alt="" style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain' }} />}
            </Box>
          ) : (
            <Button variant="outlined" startIcon={<AddPhotoAlternate />} onClick={() => inputRef.current?.click()} sx={{ py: 4 }}>
              Choose a photo or video
            </Button>
          )}
          {preview && <Button size="small" onClick={() => inputRef.current?.click()}>Choose a different one</Button>}
          <TextField
            label="Caption (optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 200))}
            helperText={`${caption.length}/200`}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Who can watch</InputLabel>
            <Select value={visibility} label="Who can watch" onChange={(e) => setVisibility(e.target.value as 'Buddies' | 'Followers')}>
              <MenuItem value="Buddies">Dive Buddies only</MenuItem>
              <MenuItem value="Followers">Followers</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            Stories disappear after 24 hours and never appear in feeds or on your profile.
          </Typography>
          {uploadFraction !== null && (
            <Box>
              <LinearProgress variant="determinate" value={Math.round(uploadFraction * 100)} />
              <Typography variant="caption" color="text.secondary">Uploading {Math.round(uploadFraction * 100)}%</Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!file || submitting}>
          {submitting ? <CircularProgress size={20} /> : 'Share story'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoryCreateDialog;
